import { useEffect, useRef } from 'react';
import { AnnotationLayer } from 'pdfjs-dist';

/**
 * PdfAnnotationLayer — Mounts PDF.js's own DOM-based AnnotationLayer next to
 * the PageCanvas. Renders /Widget form fields (textWidgetAnnotation,
 * choiceWidgetAnnotation, buttonWidgetAnnotation) with PDF.js's native
 * inputs, and /FreeText annotations as positioned `<section>` elements.
 *
 * /FreeText handling: PDF.js renders an empty container. We inject a yellow
 * background (via class) + a transparent `<input>` child, matched by
 * `data-annotation-id` against the field detected in useFormFields. Typed
 * values flow back through `onFieldValueChange(fieldId, value)`.
 *
 * /Widget handling: PDF.js renders native inputs/selects inside the section
 * and writes values into `pdfDoc.annotationStorage` as the user types — but
 * does NOT notify React, so our formValues store goes stale. To keep the
 * save pipeline (formValues → fieldData → drawText / form.setText) reading
 * fresh values, this layer attaches input/change listeners after render and
 * bridges DOM updates back to formValues via `onFieldValueChange`.
 *
 * Why bridge through formValues instead of reading annotationStorage at
 * save time: usePrecomputedSave invalidates its cache on formValues change.
 * If /Widget values lived only in PDF.js's storage, the precompute cache
 * would silently go stale on every keystroke and Save would return the
 * pre-keystroke bytes.
 *
 * Coordination with the canvas pass: PageCanvas writes annotation
 * appearance streams into `annotationCanvasMap`; AnnotationLayer consumes
 * the map and inserts per-annotation canvases as `canvas.annotationContent`
 * (hasOwnCanvas annotations only). For /FreeText form placeholders
 * `hasOwnCanvas` is usually false — the yellow paint comes from PageCanvas's
 * main canvas, layered behind our injected yellow background.
 */
export default function PdfAnnotationLayer({
  pdfDoc,
  pageNum,
  scale,
  annotationCanvasMap,
  canvasRenderToken,
  formFields,
  formValues,
  onFieldValueChange,
}) {
  const containerRef = useRef(null);

  // Refs so the post-render imperative inputs always read fresh values
  // + callback without re-running the render effect on every keystroke
  // (which would tear down + recreate the inputs, killing focus).
  const valuesRef = useRef(formValues);
  valuesRef.current = formValues;
  const onChangeRef = useRef(onFieldValueChange);
  onChangeRef.current = onFieldValueChange;

  // Push the latest values into already-mounted inputs (both our injected
  // FreeText inputs and PDF.js's native Widget inputs). Without this, an
  // external formValues update (e.g. Clear All from the toolbar) wouldn't
  // be reflected in the DOM until the next render cycle.
  useEffect(() => {
    if (!containerRef.current || !formFields) return;
    syncFieldValues(containerRef.current, formFields, formValues, pdfDoc);
  }, [formValues, formFields, pdfDoc]);

  useEffect(() => {
    if (!pdfDoc || !containerRef.current) return;
    let cancelled = false;
    const container = containerRef.current;

    (async () => {
      const page = await pdfDoc.getPage(pageNum);
      if (cancelled) return;
      const viewport = page.getViewport({ scale });
      const annotations = await page.getAnnotations();
      if (cancelled) return;

      // Build off-DOM. Overlapping canvas completions can finish out of order;
      // they must never leave visible inputs without their value listeners.
      const staging = container.cloneNode(false);
      staging.style.setProperty('--scale-factor', String(scale));
      staging.style.width = `${viewport.width}px`;
      staging.style.height = `${viewport.height}px`;

      const layer = new AnnotationLayer({
        div: staging,
        page,
        viewport,
        accessibilityManager: null,
        annotationCanvasMap: annotationCanvasMap || null,
        annotationEditorUIManager: null,
        structTreeLayer: null,
      });

      try {
        await layer.render({
          annotations,
          page,
          viewport,
          // Minimal stub — link navigation is not relevant for this layer.
          linkService: {
            externalLinkEnabled: false,
            getDestinationHash: () => '',
            getAnchorUrl: () => '',
            addLinkAttributes: () => {},
          },
          annotationStorage: pdfDoc.annotationStorage,
          // Don't expose a native input before the form scan has provided
          // its React value bridge. Early keystrokes would otherwise vanish.
          renderForms: Boolean(formFields?.length),
          imageResourcesPath: '',
        });
        if (cancelled) return;

        wireFields(staging, formFields, valuesRef, onChangeRef);
        syncFieldValues(staging, formFields, valuesRef.current, pdfDoc);
        const active = container.contains(document.activeElement) ? document.activeElement : null;
        const focusedId = active?.dataset.elementId;
        const selection = typeof active?.selectionStart === 'number' ? [active.selectionStart, active.selectionEnd] : null;
        container.style.cssText = staging.style.cssText;
        if (staging.dataset.mainRotation) container.dataset.mainRotation = staging.dataset.mainRotation;
        container.replaceChildren(...staging.childNodes);
        if (focusedId) {
          const input = container.querySelector(`[data-element-id="${focusedId}"]`);
          input?.focus({ preventScroll: true });
          if (selection && input?.setSelectionRange) input.setSelectionRange(...selection);
        }
      } catch (err) {
        if (import.meta.env.DEV) {
          console.debug('[PdfAnnotationLayer] render failed:', err);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pdfDoc, pageNum, scale, annotationCanvasMap, canvasRenderToken, formFields]);

  return (
    <div
      ref={containerRef}
      className="annotationLayer"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        pointerEvents: 'none',
        zIndex: 5,
      }}
    />
  );
}

function syncFieldValues(container, fields, values, pdfDoc) {
  for (const field of fields || []) {
    if (!field.annotId || values?.[field.id] === undefined) continue;
    const next = values[field.id];
    const input = container.querySelector(`[data-ff-id="${field.id}"]`) || container.querySelector(`[data-element-id="${field.annotId}"]`);
    if (!input) continue;
    // Clear All and programmatic changes must survive PDF.js rerenders.
    pdfDoc?.annotationStorage?.setValue(field.annotId, { value: next });
    if (input.type === 'checkbox' || input.type === 'radio') input.checked = !!next;
    else if (input.multiple && input.options) {
      const selected = new Set(Array.isArray(next) ? next : next ? [next] : []);
      for (const option of input.options) option.selected = selected.has(option.value);
    }
    else if (input.value !== String(next ?? '')) input.value = String(next ?? '');
  }
}

/**
 * Wire each detected field on this page to the corresponding DOM element
 * inside the rendered AnnotationLayer:
 *
 * - /FreeText fields: PDF.js renders an empty section; we inject yellow
 *   class + a transparent `<input>` child and listen for input events.
 * - /Widget fields: PDF.js already renders the native input/select; we
 *   only attach a bridge listener that mirrors changes back to formValues.
 */
function wireFields(container, formFields, valuesRef, onChangeRef) {
  if (!formFields || formFields.length === 0) return;
  const byAnnotId = new Map();
  for (const f of formFields) {
    if (f.annotId) byAnnotId.set(f.annotId, f);
  }
  if (byAnnotId.size === 0) return;

  // FreeText: inject yellow + input inside each empty section.
  const freeTextSections = container.querySelectorAll('.freeTextAnnotation');
  for (const section of freeTextSections) {
    const annotId = section.dataset.annotationId;
    const field = annotId && byAnnotId.get(annotId);
    if (!field || field.source !== 'freetext') continue;

    section.classList.add('scrivar-freetext-fill');

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'scrivar-freetext-input';
    input.dataset.ffId = field.id;
    input.dataset.formField = field.id;
    input.value = valuesRef.current?.[field.id] ?? '';
    input.addEventListener('input', (e) => {
      onChangeRef.current?.(field.id, e.target.value);
    });
    section.appendChild(input);
  }

  // Widget: bridge PDF.js's native input/select/checkbox/radio events back
  // to formValues. PDF.js writes the value into its own annotationStorage,
  // but doesn't notify React — without this listener the save pipeline
  // would read stale formValues and the cached precompute bytes would
  // never invalidate on a keystroke.
  const nativeInputs = container.querySelectorAll(
    '.textWidgetAnnotation input, .textWidgetAnnotation textarea, .choiceWidgetAnnotation select, .buttonWidgetAnnotation input',
  );
  for (const el of nativeInputs) {
    const annotId = el.dataset.elementId;
    const field = annotId && byAnnotId.get(annotId);
    if (!field || field.source !== 'widget') continue;

    const handler = () => {
      const value = (el.type === 'checkbox' || el.type === 'radio')
        ? el.checked
        : el.multiple ? [...el.selectedOptions].map((option) => option.value) : el.value;
      onChangeRef.current?.(field.id, value);
    };
    // 'input' fires on every keystroke for text inputs; 'change' covers
    // checkbox / radio / select.
    el.addEventListener('input', handler);
    el.addEventListener('change', handler);
  }
}
